import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { RecoverInput } from '@lub0v/santa-utils';
import {
  AuthFrame,
  Button,
  Checkbox,
  HintError,
  EmailInput,
  Input,
  Link,
  OtpChallenge,
  showDialog,
  Stack,
  Typography,
} from '@src/components';
import { useEnterOrder } from '@src/forms/useEnterOrder';
import { recoverWithEmail, recoverWithCode, verifyOtpCode } from '@src/pages/auth/auth';
import { supportEmail } from '@src/site';
import { useValidatedForm } from '@src/validation';

const RECOVER_ENTER = ['recover-email', 'recover-consent', 'recover-submit'] as const;
const CODE_ENTER = ['recover-email', 'recover-code', 'recover-consent', 'recover-submit'] as const;

export function RecoverPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const form = useValidatedForm(RecoverInput, { email: '' });
  const [method, setMethod] = useState<'email' | 'code'>('email');
  const [code, setCode] = useState('');
  const enter = useEnterOrder(method === 'code' ? CODE_ENTER : RECOVER_ENTER);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [sentEmail, setSentEmail] = useState('');

  async function submit(email: string) {
    setError(null);
    setBusy(true);
    try {
      if (method === 'code') {
        setError(await recoverWithCode(navigate, email, code));
        return;
      }
      const result = await recoverWithEmail(email);
      if ('token' in result) {
        setSentEmail(email);
        setToken(result.token);
        return;
      }
      setError(result.error);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'recover.failed');
    } finally {
      setBusy(false);
    }
  }

  if (token) {
    return (
      <AuthFrame
        title={t('otp.title')}
        testId="recover-otp"
        fill
        footer={
          <Stack gap={4} align="center">
            <Link
              variant="plain"
              testId="recover-change-email"
              onClick={() => {
                setToken(null);
                setSentEmail('');
              }}
            >
              {t('otp.changeEmail')}
            </Link>
            <Link
              variant="plain"
              testId="recover-otp-use-code"
              onClick={() => {
                setToken(null);
                setSentEmail('');
                setMethod('code');
                setError(null);
              }}
            >
              {t('recover.useCode')}
            </Link>
          </Stack>
        }
      >
        <OtpChallenge
          challengeToken={token}
          testId="recover-otp-code"
          onVerify={(token, code) => verifyOtpCode(navigate, token, code)}
          hint={
            <Trans
              i18nKey="recover.sent"
              values={{ email: sentEmail }}
              components={{ email: <Typography as="span" role="body/strong" /> }}
            />
          }
        />
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title={t('recover.title')}
      subtitle={t('recover.options')}
      testId="recover"
      footer={
        <Link
          variant="quiet"
          testId="recover-troubles"
          onClick={() => {
            const email = supportEmail();
            showDialog({
              title: t('recover.troubles'),
              submit: t('dialog.ok'),
              closeLabel: t('dialog.close'),
              testId: 'recover-help',
              children: (
                <Trans
                  i18nKey="recover.helpBody"
                  values={{ email }}
                  components={{
                    email: <Link href={`mailto:${email}`} variant="inline" />,
                  }}
                />
              ),
            });
          }}
        >
          {t('recover.troubles')}
        </Link>
      }
    >
      <Stack gap={6}>
        <EmailInput
          label={t('login.email')}
          autoComplete="email"
          hint={t(method === 'code' ? 'recover.codeEmailHint' : 'recover.hint')}
          testId="recover-email"
          {...form.field('email')}
          {...enter.bind('recover-email')}
        />
        {method === 'code' && (
          <Input
            label={t('recover.codeLabel')}
            hint={t('recover.codeHint')}
            value={code}
            onValueChange={setCode}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            maxLength={64}
            testId="recover-code"
            {...enter.bind('recover-code')}
          />
        )}
        <Checkbox
          checked={consent}
          onCheckedChange={setConsent}
          testId="recover-consent"
          {...enter.bind('recover-consent')}
          label={
            <Trans
              i18nKey="login.consent"
              components={{
                terms: <Link href="/terms" variant="inline" />,
                privacy: <Link href="/policy" variant="inline" />,
              }}
            />
          }
        />
        <HintError error={error} testId="recover-error" />
        <Button
          type="button"
          variant="primary"
          size="large"
          fullWidth
          disabled={busy || !consent || !form.filled || (method === 'code' && !code.trim())}
          loading={busy}
          testId="recover-submit"
          onClick={() => void form.submit((values) => submit(values.email))}
        >
          {t(method === 'code' ? 'recover.codeSubmit' : 'recover.submit')}
        </Button>
        <Button
          variant="secondary"
          fullWidth
          disabled={busy}
          testId="recover-switch-method"
          onClick={() => {
            setMethod(method === 'email' ? 'code' : 'email');
            setCode('');
            setError(null);
          }}
        >
          {t(method === 'email' ? 'recover.useCode' : 'recover.useEmail')}
        </Button>
      </Stack>
    </AuthFrame>
  );
}
